import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { SwapIcon, TrashIcon } from './icons';

export interface ConfirmOptions {
  title: string;
  body: ReactNode;
  /** Names the action: "Delete room", not "OK". */
  confirmLabel: string;
  cancelLabel?: string;
  /**
   * `danger` is for what cannot be taken back: it is drawn in red and opens
   * with Cancel focused, so a reflexive Enter does nothing. `neutral` opens
   * on the action itself.
   */
  tone?: 'danger' | 'neutral';
  /** A short fact to show on its own line, e.g. "Opus 5 → Sonnet 5". */
  detail?: ReactNode;
}

type Confirm = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

/**
 * Awaitable in-app replacement for window.confirm():
 *
 *   if (!(await confirm({ title, body, confirmLabel }))) return;
 */
export function useConfirm(): Confirm {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return confirm;
}

interface Pending extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending>();

  const confirm = useCallback<Confirm>(
    (opts) =>
      new Promise<boolean>((resolve) => {
        // One question at a time; a second one answers the first with "no".
        setPending((prev) => {
          prev?.resolve(false);
          return { ...opts, resolve };
        });
      }),
    [],
  );

  const settle = useCallback((ok: boolean) => {
    setPending((prev) => {
      prev?.resolve(ok);
      return undefined;
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && <ConfirmDialog key={pending.title} {...pending} onSettle={settle} />}
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({
  title,
  body,
  detail,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'neutral',
  onSettle,
}: ConfirmOptions & { onSettle: (ok: boolean) => void }) {
  const id = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    (tone === 'danger' ? cancelRef : confirmRef).current?.focus();
    return () => returnTo?.focus?.();
  }, [tone]);

  useEffect(() => {
    // Capture on window runs before every other keydown listener, so Escape
    // answers this question without also closing the drawer or dialog
    // underneath it. Tab is kept between the two buttons.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        onSettle(false);
      } else if (e.key === 'Tab') {
        e.preventDefault();
        const next = document.activeElement === cancelRef.current ? confirmRef : cancelRef;
        next.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onSettle]);

  return (
    <div
      className="scrim scrim--top"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onSettle(false);
      }}
    >
      <div
        className={`confirm confirm--${tone}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-body`}
      >
        <span className="confirm__badge">
          {tone === 'danger' ? <TrashIcon size={16} /> : <SwapIcon size={16} />}
        </span>
        <div className="confirm__text">
          <h2 className="confirm__title" id={`${id}-title`}>
            {title}
          </h2>
          <p className="confirm__body" id={`${id}-body`}>
            {body}
          </p>
          {detail && <div className="confirm__detail">{detail}</div>}
        </div>
        <div className="confirm__actions">
          <button ref={cancelRef} className="button" onClick={() => onSettle(false)}>
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            className={`button ${tone === 'danger' ? 'button--danger' : 'button--primary'}`}
            onClick={() => onSettle(true)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
